import React, { useState, useMemo } from 'react';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function getLast12MonthsStatements() {
  const now = new Date();
  const periods = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = d.getMonth();
    const id = `${y}-${String(m + 1).padStart(2, '0')}`;
    const label = `${MONTH_NAMES[m]} ${y}`;
    periods.push({ id, label });
  }
  return periods;
}

export default function Statements({ accounts, userEmail }) {
  const statementPeriods = useMemo(() => getLast12MonthsStatements(), []);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleDownload = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);

    // Simulate preparing and sending statement to registered email
    await new Promise(resolve => setTimeout(resolve, 2000));

    setSuccess(true);
    setLoading(false);
  };

  return (
    <div>
      {loading && (
        <div className="mb-6 p-4 bg-primary-50 text-primary-800 rounded-xl border border-primary-200 flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          <div>
            <p className="font-medium">Preparing your statement…</p>
            <p className="text-sm text-primary-700 mt-0.5">
              It will be sent to your registered email{userEmail ? ` (${userEmail})` : ''}.
            </p>
          </div>
        </div>
      )}

      {success && !loading && (
        <div className="mb-6 p-4 bg-green-50 text-green-800 rounded-xl border border-green-200 flex items-start gap-3">
          <span className="text-2xl flex-shrink-0" aria-hidden>✓</span>
          <div>
            <p className="font-medium">Statement sent to your registered email</p>
            <p className="text-sm text-green-700 mt-1">
              {userEmail
                ? `We've sent your statement to ${userEmail}. Check your inbox.`
                : "We've sent your statement to your registered email address. Check your inbox."}
            </p>
          </div>
        </div>
      )}

      <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
        <p className="text-sm text-yellow-800">
          Last 12 months of statements are available below. For older statements, please contact support@claireoakscu.com
        </p>
      </div>

      <form onSubmit={handleDownload} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Account
          </label>
          <select
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
            className="w-full p-3 border border-gray-200 rounded-xl focus:ring-emerald-500 focus:border-emerald-500 bg-gray-50 hover:bg-white transition-colors duration-200"
            disabled={loading}
            required
          >
            <option value="">Choose an account</option>
            {accounts.map((account) => (
              <option key={account.type} value={account.type}>
                {account.type} (*{account.accountNumber.slice(-4)})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Statement Period
          </label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="w-full p-3 border border-gray-200 rounded-xl focus:ring-emerald-500 focus:border-emerald-500 bg-gray-50 hover:bg-white transition-colors duration-200"
            disabled={loading}
            required
          >
            <option value="">Select period</option>
            {statementPeriods.map((period) => (
              <option key={period.id} value={period.id}>
                {period.label}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          disabled={loading || !selectedAccount || !selectedPeriod}
          className={`w-full py-3 px-4 rounded-xl text-white font-medium
            ${loading || !selectedAccount || !selectedPeriod
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
            } transition-colors duration-200`}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
              Processing...
            </div>
          ) : (
            'Download Statement'
          )}
        </button>

        <div className="mt-6 bg-gray-50 rounded-xl p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Recent Statements</h3>
          <div className="space-y-2">
            {statementPeriods.slice(0, 3).map((period) => (
              <div 
                key={period.id}
                className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-primary-50 transition-colors duration-200 cursor-pointer group"
              >
                <div className="flex items-center">
                  <span className="text-xl mr-3">📄</span>
                  <span className="text-sm text-gray-900">{period.label}</span>
                </div>
                <button className="text-primary-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  Download →
                </button>
              </div>
            ))}
          </div>
        </div>

        <p className="text-sm text-gray-500 mt-4">
          Statements are generated as PDFs and sent to your registered email address.
        </p>
      </form>
    </div>
  );
} 